import { CommonModule } from '@angular/common';
import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SliderBase } from '@nimbus-library/features/shared/components/slider/domain/slider-base';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { RangeSliderChangeEvent, RangeSliderValue } from '@nimbus-library/features/shared/components/slider/domain/slider-change.event';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-range-slider',
  host: {
    '(focus)': '_onFocus()',
    '(mouseenter)': '_onMouseenter()',
    '(selectstart)': '$event.preventDefault()',
    class: 'range-slider',
    '[class.range-slider-disabled]': 'disabled',
    '[class.range-slider-sliding]': '_isSliding',
  },
  templateUrl: './range-slider.component.html',
  styleUrl: './range-slider.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class RangeSliderComponent extends SliderBase implements OnDestroy {
  @ViewChild('sliderWrapper', { static: true })
  private _sliderWrapper: ElementRef;

  @Input()
  public disabled: boolean = false;

  @Output()
  public readonly valueChange: EventEmitter<RangeSliderValue> = new EventEmitter<RangeSliderValue>();

  @Output()
  public readonly eventChange: EventEmitter<RangeSliderChangeEvent<RangeSliderComponent>> = new EventEmitter<
    RangeSliderChangeEvent<RangeSliderComponent>
  >();

  @Output()
  public readonly eventInput: EventEmitter<RangeSliderChangeEvent<RangeSliderComponent>> = new EventEmitter<
    RangeSliderChangeEvent<RangeSliderComponent>
  >();

  private readonly _hundredPercent: number = 100;
  private readonly _activeEventOptions: boolean | AddEventListenerOptions = normalizePassiveListenerOptions({ passive: false });
  private _min: number = 0;
  private _max: number = 100;
  private _step: number = 1;
  private _lowValue: number = 0;
  private _highValue: number = 0;
  private _lowPercent: number = 0;
  private _highPercent: number = 0;
  private _activeThumb: 'high' | 'low' | null = null;
  private _sliderDimensions: ClientRect | null = null;
  private _valueOnSlideStart: RangeSliderValue | null = null;
  public _isSliding: boolean = false;

  public teamsClient$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((x) => x !== ClientType.Browser),
      )
    : of(false);

  @Input()
  public get max(): number {
    return this._max;
  }

  public set max(v: number) {
    this._max = v;
    this._recalculatePercentages();
    this._changeDetectorRef.markForCheck();
  }

  @Input()
  public get min(): number {
    return this._min;
  }

  public set min(v: number) {
    this._min = v;
    this._recalculatePercentages();
    this._changeDetectorRef.markForCheck();
  }

  @Input()
  public get step(): number {
    return this._step;
  }

  public set step(v: number) {
    this._step = v;
    this._changeDetectorRef.markForCheck();
  }

  @Input()
  public get value(): RangeSliderValue {
    return [this._lowValue, this._highValue];
  }

  public set value(v: RangeSliderValue) {
    const [low, high] = v && v.length === 2 ? v : [this._min, this._min];
    this._lowValue = Math.min(low, high);
    this._highValue = Math.max(low, high);
    this._recalculatePercentages();
    this._changeDetectorRef.markForCheck();
  }

  public get lowPercent(): number {
    return this._clamp(this._lowPercent);
  }

  public get highPercent(): number {
    return this._clamp(this._highPercent);
  }

  public constructor(
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
    elementRef: ElementRef,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _ngZone?: NgZone,
  ) {
    super(elementRef);

    this._runOutsizeZone(() => {
      const element = elementRef.nativeElement;
      element.addEventListener('mousedown', this._pointerDown, this._activeEventOptions);
      element.addEventListener('touchstart', this._pointerDown, this._activeEventOptions);
    });
  }

  public ngOnDestroy(): void {
    const element = this._elementRef.nativeElement;
    element.removeEventListener('mousedown', this._pointerDown, this._activeEventOptions);
    element.removeEventListener('touchstart', this._pointerDown, this._activeEventOptions);
    this._removeGlobalEvents();
  }

  public _onMouseenter(): void {
    if (this.disabled) {
      return;
    }
    this._sliderDimensions = this._getSliderDimensions();
  }

  public _onFocus(): void {
    this._sliderDimensions = this._getSliderDimensions();
  }

  public _thumbContainerStyles(thumb: 'high' | 'low'): { [key: string]: string } {
    const percent = thumb === 'low' ? this.lowPercent : this.highPercent;
    const offset = (1 - percent) * this._hundredPercent;
    return {
      transform: `translateX(-${offset}%)`,
    };
  }

  public _getTrackFillStyles(): { [key: string]: string } {
    const left = this.lowPercent * this._hundredPercent;
    const width = (this.highPercent - this.lowPercent) * this._hundredPercent;
    return {
      left: `${left}%`,
      width: `${width}%`,
    };
  }

  private _pointerDown = (event: TouchEvent | MouseEvent): void => {
    if (this.disabled || this._isSliding || (!this.isTouchEvent(event) && event.button !== 0)) {
      return;
    }

    this._runInsideZone(() => {
      const pointerPosition = this.getPointerPositionOnPage(event);

      this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
        if (isTeamsClient) {
          const browserScale = this._getBrowserScalePercentage();
          pointerPosition.x = (pointerPosition.x / this._hundredPercent) * browserScale;
          pointerPosition.y = (pointerPosition.y / this._hundredPercent) * browserScale;
        }
      });

      this._isSliding = true;
      event.preventDefault();
      this._focusHostElement();
      this._onMouseenter();
      this._bindGlobalEvents(event);
      this._activeThumb = this._hitTestThumb(pointerPosition);

      const oldValue = this.value;
      this._updateValueFromPosition(pointerPosition);
      this._valueOnSlideStart = oldValue;

      if (oldValue[0] !== this.value[0] || oldValue[1] !== this.value[1]) {
        this._emitInputEvent();
      }
    });
  };

  private _pointerMove = (event: TouchEvent | MouseEvent): void => {
    if (!this._isSliding) {
      return;
    }
    event.preventDefault();
    const oldValue = this.value;
    const pointerPosition = this.getPointerPositionOnPage(event);

    this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
      if (isTeamsClient) {
        const browserScale = this._getBrowserScalePercentage();
        pointerPosition.x = (pointerPosition.x / this._hundredPercent) * browserScale;
        pointerPosition.y = (pointerPosition.y / this._hundredPercent) * browserScale;
      }
      this._updateValueFromPosition(pointerPosition);

      if (oldValue[0] !== this.value[0] || oldValue[1] !== this.value[1]) {
        this._emitInputEvent();
      }
    });
  };

  private _pointerUp = (event: TouchEvent | MouseEvent): void => {
    if (!this._isSliding) {
      return;
    }

    event.preventDefault();
    this._removeGlobalEvents();
    const valueOnSlideStart = this._valueOnSlideStart;
    this._valueOnSlideStart = null;
    this._isSliding = false;
    this._activeThumb = null;

    if (valueOnSlideStart && !this.disabled && (valueOnSlideStart[0] !== this.value[0] || valueOnSlideStart[1] !== this.value[1])) {
      this._emitChangeEvent();
    }
  };

  private _hitTestThumb(pointerPosition: { x: number; y: number }): 'high' | 'low' {
    if (!this._sliderDimensions) {
      return 'high';
    }
    const percent = this._clamp((pointerPosition.x - this._sliderDimensions.left) / this._sliderDimensions.width);

    if (this._lowPercent === this._highPercent) {
      return percent >= this._lowPercent ? 'high' : 'low';
    }

    const distanceToLow = Math.abs(percent - this._lowPercent);
    const distanceToHigh = Math.abs(percent - this._highPercent);
    return distanceToLow <= distanceToHigh ? 'low' : 'high';
  }

  private _getBrowserScalePercentage(): number {
    const isScaled = (this._elementRef.nativeElement as HTMLDivElement).classList.contains('scaled');
    return isScaled ? Math.round(window.devicePixelRatio * this._hundredPercent) : this._hundredPercent;
  }

  private _updateValueFromPosition(pos: { x: number; y: number }): void {
    if (!this._sliderDimensions || !this._activeThumb) {
      return;
    }

    const offset = this._sliderDimensions.left;
    const size = this._sliderDimensions.width;
    const percent = this._clamp((pos.x - offset) / size);

    let rawValue: number;
    if (percent === 0) {
      rawValue = this.min;
    } else if (percent === 1) {
      rawValue = this.max;
    } else {
      const exactValue = this._calculateValue(percent);
      rawValue = Math.round((exactValue - this.min) / this.step) * this.step + this.min;
    }
    rawValue = this._clamp(rawValue, this.min, this.max);

    if (this._activeThumb === 'low') {
      this._lowValue = Math.min(rawValue, this._highValue);
    } else {
      this._highValue = Math.max(rawValue, this._lowValue);
    }

    this._recalculatePercentages();
    this._changeDetectorRef.markForCheck();
  }

  private _recalculatePercentages(): void {
    this._lowPercent = this._calculatePercentage(this._lowValue);
    this._highPercent = this._calculatePercentage(this._highValue);
  }

  private _calculatePercentage(value: number): number {
    return this.max === this.min ? 0 : (value - this.min) / (this.max - this.min);
  }

  private _calculateValue(percentage: number): number {
    return this.min + percentage * (this.max - this.min);
  }

  private _clamp(value: number, min = 0, max = 1): number {
    return Math.max(min, Math.min(value, max));
  }

  private _emitChangeEvent(): void {
    this.valueChange.emit(this.value);
    this.eventChange.emit(this._createChangeEvent());
  }

  private _emitInputEvent(): void {
    this.eventInput.emit(this._createChangeEvent());
  }

  private _createChangeEvent(): RangeSliderChangeEvent<RangeSliderComponent> {
    const result = new RangeSliderChangeEvent<RangeSliderComponent>();
    result.source = this;
    result.value = this.value;
    return result;
  }

  private _bindGlobalEvents(triggerEvent: TouchEvent | MouseEvent): void {
    if (typeof document !== 'undefined' && document) {
      const body = document.body;
      const isTouch = this.isTouchEvent(triggerEvent);
      const moveEventName = isTouch ? 'touchmove' : 'mousemove';
      const endEventName = isTouch ? 'touchend' : 'mouseup';
      body.addEventListener(moveEventName, this._pointerMove, this._activeEventOptions);
      body.addEventListener(endEventName, this._pointerUp, this._activeEventOptions);

      if (isTouch) {
        body.addEventListener('touchcancel', this._pointerUp, this._activeEventOptions);
      }
    }
  }

  private _removeGlobalEvents(): void {
    if (typeof document !== 'undefined' && document) {
      const body = document.body;
      body.removeEventListener('mousemove', this._pointerMove, this._activeEventOptions);
      body.removeEventListener('mouseup', this._pointerUp, this._activeEventOptions);
      body.removeEventListener('touchmove', this._pointerMove, this._activeEventOptions);
      body.removeEventListener('touchend', this._pointerUp, this._activeEventOptions);
      body.removeEventListener('touchcancel', this._pointerUp, this._activeEventOptions);
    }
  }

  private _getSliderDimensions() {
    return this._sliderWrapper ? this._sliderWrapper.nativeElement.getBoundingClientRect() : null;
  }

  private _focusHostElement(options?: FocusOptions): void {
    this._elementRef.nativeElement.focus(options);
  }

  private _runInsideZone(fn: () => any): void {
    this._ngZone ? this._ngZone.run(fn) : fn();
  }

  private _runOutsizeZone(fn: () => any): void {
    this._ngZone ? this._ngZone.runOutsideAngular(fn) : fn();
  }

  private isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
    return event.type[0] === 't';
  }

  private getPointerPositionOnPage(event: MouseEvent | TouchEvent): { x: number; y: number } {
    const point = this.isTouchEvent(event) ? event.touches[0] || event.changedTouches[0] : event;
    return { x: point.clientX, y: point.clientY };
  }
}
